import { DateTextInput, Form, MaskedPhoneNumberInput, MaskedPhoneNumberInputSchema, SchemaBuilder, SelectInput, TextInput, useHookForm } from '@aspendental/shared-forms-web';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { type FieldValues } from 'react-hook-form';
import { GUARANTOR_RELATIONSHIPS } from '@src/constants';
import { useScrollToFirstError } from '@src/hooks/useScrollToFirstError';
import { GuarantorInformationSchema, useFormSectionStore, useGuarantorInformationFormStore } from '@src/store';
import { dateInvalid } from '@src/utils/date-parser';
import { formatPhoneNumber } from '@src/utils/format-phone-number';
import { unacceptableInputs } from '@src/utils/offensive-words';
const GuarantorInformationForm = ({
  formId
}: {
  formId: string;
}) => {
  const [offensiveInputsFound, setOffensiveInputsFound] = useState(false);
  const {
    firstName,
    lastName,
    dateOfBirth,
    zipCode,
    email,
    phoneNumber,
    relationshipToPatient,
    setFirstName,
    setLastName,
    setDateOfBirth,
    setZipCode,
    setEmail,
    setPhoneNumber,
    setRelationshipToPatient
  } = useGuarantorInformationFormStore(store => store);
  const completeActiveNode = useFormSectionStore(store => store.completeActiveNode);
  const {
    t
  } = useTranslation('guarantorInfo');
  const schema = SchemaBuilder([{
    name: 'firstName',
    validation: GuarantorInformationSchema.firstName
  }, {
    name: 'lastName',
    validation: GuarantorInformationSchema.lastName
  }, {
    name: 'dateOfBirth',
    validation: GuarantorInformationSchema.dateOfBirth
  }, {
    name: 'zipCode',
    validation: GuarantorInformationSchema.zipCode
  }, {
    name: 'email',
    validation: GuarantorInformationSchema.email
  }, {
    name: 'phoneNumber',
    validation: MaskedPhoneNumberInputSchema.options.default
  }, {
    name: 'relationshipToPatient',
    validation: GuarantorInformationSchema.relationshipToPatient
  }]);
  const initialValueDateOfBirth = dateInvalid(dateOfBirth) ? '' : format(dateOfBirth as Date, 'MM/dd/yyyy');
  const defaultValues = {
    firstName,
    lastName,
    dateOfBirth: initialValueDateOfBirth,
    zipCode,
    email,
    phoneNumber: formatPhoneNumber(phoneNumber) || phoneNumber,
    relationshipToPatient
  };
  const form = useHookForm({
    schema,
    defaultValues,
    shouldFocusError: false
  });
  useScrollToFirstError(form as any, formId);
  const handleSubmit = (fields: FieldValues) => {
    const unacceptableFields = unacceptableInputs([{
      name: 'guarantorFirstName',
      value: fields.firstName
    }, {
      name: 'guarantorLastName',
      value: fields.lastName
    }]);
    if (unacceptableFields.length) {
      console.error('offensive inputs detected: ', unacceptableFields.join(', '));
      setOffensiveInputsFound(true);
      return;
    } else {
      setOffensiveInputsFound(false);
    }
    setFirstName(fields.firstName);
    setLastName(fields.lastName);
    setDateOfBirth(fields.dateOfBirth);
    setZipCode(fields.zipCode);
    setEmail(fields.email);
    setPhoneNumber(fields.phoneNumber);
    setRelationshipToPatient(fields.relationshipToPatient);
    completeActiveNode();
  };
  return <Form form={form as any} onSubmit={handleSubmit} formId={formId} data-sentry-element="Form" data-sentry-component="GuarantorInformationForm" data-sentry-source-file="GuarantorInformationForm.tsx">
			<Grid container spacing={2} data-sentry-element="Grid" data-sentry-source-file="GuarantorInformationForm.tsx">
				<Grid item xs={12} data-sentry-element="Grid" data-sentry-source-file="GuarantorInformationForm.tsx">
					<Alert severity="info" variant="flatSecondary" data-sentry-element="Alert" data-sentry-source-file="GuarantorInformationForm.tsx">
						<Typography color="text.secondary" data-sentry-element="Typography" data-sentry-source-file="GuarantorInformationForm.tsx">{t('parentGuardian')}</Typography>
					</Alert>
				</Grid>
				<Grid item xs={12} sm={6} data-sentry-element="Grid" data-sentry-source-file="GuarantorInformationForm.tsx">
					<TextInput name="firstName" label={t('firstNameLabel')} data-sentry-element="TextInput" data-sentry-source-file="GuarantorInformationForm.tsx" />
				</Grid>
				<Grid item xs={12} sm={6} data-sentry-element="Grid" data-sentry-source-file="GuarantorInformationForm.tsx">
					<TextInput name="lastName" label={t('lastNameLabel')} data-sentry-element="TextInput" data-sentry-source-file="GuarantorInformationForm.tsx" />
				</Grid>
				<Grid item xs={12} sm={6} data-sentry-element="Grid" data-sentry-source-file="GuarantorInformationForm.tsx">
					<DateTextInput name="dateOfBirth" label={t('dobLabel')} required data-sentry-element="DateTextInput" data-sentry-source-file="GuarantorInformationForm.tsx" />
				</Grid>
				<Grid item xs={12} sm={6} data-sentry-element="Grid" data-sentry-source-file="GuarantorInformationForm.tsx">
					<TextInput name="zipCode" label={t('zipCodeLabel')} maxLength={5} data-sentry-element="TextInput" data-sentry-source-file="GuarantorInformationForm.tsx" />
				</Grid>
				<Grid item xs={12} sm={6} data-sentry-element="Grid" data-sentry-source-file="GuarantorInformationForm.tsx">
					<TextInput name="email" label={t('emailLabel')} enforceAlphaNumeric={false} data-sentry-element="TextInput" data-sentry-source-file="GuarantorInformationForm.tsx" />
				</Grid>
				<Grid item xs={12} sm={6} data-sentry-element="Grid" data-sentry-source-file="GuarantorInformationForm.tsx">
					<MaskedPhoneNumberInput name="phoneNumber" label={t('phoneNumLabel')} data-sentry-element="MaskedPhoneNumberInput" data-sentry-source-file="GuarantorInformationForm.tsx" />
				</Grid>
				<Grid item xs={12} sm={6} data-sentry-element="Grid" data-sentry-source-file="GuarantorInformationForm.tsx">
					<SelectInput name="relationshipToPatient" label={t('patientRelationship')} options={GUARANTOR_RELATIONSHIPS.map(option => ({
          label: option,
          value: option
        }))} data-sentry-element="SelectInput" data-sentry-source-file="GuarantorInformationForm.tsx" />
				</Grid>
				{offensiveInputsFound && <Grid item xs={12}>
						<Alert severity="error" variant="flatError">
							<Typography color="text.secondary">{t('offensiveEntries')}</Typography>
						</Alert>
					</Grid>}
			</Grid>
		</Form>;
};
export default GuarantorInformationForm;